import { useEffect, useRef } from "react";

/**
 * Persist & restore the window's scroll position across unmount / remount.
 *
 * Use this on long pages whose component is unmounted when navigating away
 * (e.g. tab switches that conditionally render the panel) so that returning
 * to the page lands the user back at where they were scrolled to.
 *
 * @param {string} key - unique sessionStorage key for this view.
 * @param {boolean} ready - when true, content is rendered and it's safe to
 *                          restore the saved scroll position.
 */
export default function useScrollRestore(key, ready = true) {
  const restoredRef = useRef(false);

  // Track scroll position continuously so the latest value is captured even
  // if React's effect cleanup fires after the page has already been
  // scrolled to top by the browser.
  useEffect(() => {
    if (!key) return undefined;
    const onScroll = () => {
      try {
        sessionStorage.setItem(key, String(window.scrollY));
      } catch {
        // ignore storage errors
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [key]);

  // Restore once content is ready.
  useEffect(() => {
    if (!key || !ready || restoredRef.current) return;
    let saved = 0;
    try {
      saved = Number(sessionStorage.getItem(key) || 0);
    } catch {
      saved = 0;
    }
    if (saved > 0) {
      // Wait one frame so the rendered content has its full height.
      requestAnimationFrame(() => {
        window.scrollTo(0, saved);
      });
    }
    restoredRef.current = true;
  }, [key, ready]);
}
